import { ReactNode } from "react";

import { Navigate, NoContextOutlet, generatePath, useParams } from "src/router";
import { PageContentSkeleton } from "src/components/loading";
import { Entitlements, useEntitlements } from "src/hooks/use-entitlement";

type BooleanEntitlement = {
  [k in keyof Entitlements]: Entitlements[k] extends boolean ? k : never;
}[keyof Entitlements];

type Props = {
  /**
   * The entitlement to check. Must be a boolean entitlement.
   */
  entitlement: BooleanEntitlement;
  /**
   * Where to redirect if the entitlement check fails.
   * Supports route params (e.g., "/models/:id" will be resolved with current params).
   */
  redirect: string;
  /**
   * If provided, renders children. If not provided, renders an Outlet.
   */
  children?: ReactNode;
};

/**
 * A route guard that checks if the user has a specific entitlement.
 * If the entitlement check fails, redirects to the specified path.
 *
 * @example
 * // With children
 * <EntitledRoute entitlement="analytics" redirect="/">
 *   <MetricsPage />
 * </EntitledRoute>
 *
 * @example
 * // Without children (renders Outlet for nested routes)
 * <EntitledRoute entitlement="journeys" redirect="/templates" />
 */
export function EntitledRoute({ entitlement, redirect, children }: Props) {
  const params = useParams();
  const { data: entitlementsData, isLoading } = useEntitlements(true, true);

  if (isLoading) {
    return <PageContentSkeleton />;
  }

  const isEntitled = entitlementsData.entitlements[entitlement];

  if (!isEntitled) {
    const to = generatePath(redirect, params);
    return <Navigate to={to} replace />;
  }

  return children ? <>{children}</> : <NoContextOutlet />;
}
